// Marketplace API helpers — thin wrappers around /api/vendors.

import { BETA_SECRET } from "../lib/api";

const jsonHeaders = () => ({
  "Content-Type": "application/json",
  "x-beta-token": BETA_SECRET,
});

// List active vendors of a given type. Optionally filter by ZIP.
// Throws an Error with `.detail` describing the server-side reason on failure
// so the UI can show actionable messaging (e.g. "migration not run").
export const listVendors = async ({ type, zip, limit } = {}) => {
  const params = new URLSearchParams();
  if (type)  params.set("type", type);
  if (zip)   params.set("zip", zip);
  if (limit) params.set("limit", String(limit));
  const res = await fetch(`/api/vendors?${params}`, { headers: jsonHeaders() });
  if (!res.ok) {
    let detail = null;
    try { detail = (await res.json())?.error; } catch {}
    const err = new Error(`listVendors ${res.status}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  const data = await res.json();
  return data.rows || [];
};

// Ask the server to AI-generate a quote for vendor × this property/listing.
// Server persists the quote to vendor_quotes and returns the parsed object.
export const generateQuote = async (payload) => {
  const res = await fetch("/api/vendors", {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `generateQuote ${res.status}`);
  }
  return res.json();
};

// AI ranks vendors of a given type for this property. Returns { top, all }
// where top is the AI-picked ordered short-list with reasoning per pick.
export const rankVendors = async ({ type, address, facts, zip }) => {
  const res = await fetch("/api/vendors", {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ action: "rank", type, address, facts, zip }),
  });
  if (!res.ok) throw new Error(`rankVendors ${res.status}`);
  return res.json();
};

// AI drafts a counter-offer email for an existing quote.
export const counterQuote = async ({ quote_id, target_total, target_lead_days, scope_changes }) => {
  const res = await fetch("/api/vendors", {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ action: "counter", quote_id, target_total, target_lead_days, scope_changes }),
  });
  if (!res.ok) throw new Error(`counterQuote ${res.status}`);
  return res.json();
};

// Mark a quote as accepted in the DB. Returns { ok, contact: { name, email, phone } }
// so the UI can reveal vendor contact details immediately after acceptance.
export const acceptQuote = async ({ quote_id, user_id }) => {
  const res = await fetch("/api/vendors", {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ action: "accept", quote_id, user_id }),
  });
  if (!res.ok) throw new Error(`acceptQuote ${res.status}`);
  return res.json();
};

// Book a time slot for an accepted quote. Returns { ok, scheduled_at }.
export const scheduleBooking = async ({ quote_id, scheduled_at, notes, seller_email }) => {
  const res = await fetch("/api/vendors", {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ action: "schedule", quote_id, scheduled_at, notes, seller_email }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `scheduleBooking ${res.status}`);
  }
  return res.json();
};

// Send a direct inquiry to a vendor (no quote needed). Public — no beta token.
export const sendInquiry = async ({ vendor_id, name, email, phone, message, property_address, quote_id, stage_id }) => {
  const res = await fetch("/api/vendors", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "inquire", vendor_id, name, email, phone, message, property_address, quote_id, stage_id }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `sendInquiry ${res.status}`);
  }
  return res.json();
};
